<template>
  <div :class="[props.xsFullWidth && 'col-xs-12 col-sm-auto', props.stretch && 'flex-stretch', props.class]">
    <q-btn
      no-caps
      :no-wrap="props.noWrap"
      unelevated
      :class="btnClasses"
      :flat="props.flat"
      :style="props.progressTime ? { '--animation-duration': `${props.progressTime}s` } : undefined"
      v-bind="{ ...$attrs, ...stylingAttrs, disable: isReadOnly }"
      :padding="buttonPadding"
    >
      <template #default>
        <template v-if="props.icon">
          <!-- size should probably be based on props.size, but right now that's too big -->
          <re-icon
            :name="props.icon"
            size="xs"
            :color="stylingAttrs.textColor"
            :class="{ 'on-left': !onlyContainsIcon }"
          />
        </template>

        <span v-if="props.label">{{ props.label }}</span>
        <!-- Render slot content (e.g., q-badge) alongside the label -->
        <slot />

        <template v-if="props.iconRight">
          <!-- size should probably be based on props.size, but right now that's too big -->
          <re-icon
            :name="props.iconRight"
            size="xs"
            :color="stylingAttrs.textColor"
            :class="{ 'on-right': !onlyContainsIcon }"
          />
        </template>
        <!-- Quasar sets the button width based on the default slot,
             irrespective of the content of the loading slot, even in loading state.
             For that reason, when there is no label but there is a loadingLabel,
             we copy the content of the loading slot in the default slot just to set the right width. -->
        <template v-if="!props.label && loadingLabel">
          <span class="flex no-wrap">
            <q-spinner-facebook
              size="xs"
              class="on-left"
            />

            <span>{{ loadingLabel }}</span>
          </span>
        </template>
        <!-- Slot for invisible overlays like q-popup-proxy, q-menu, q-tooltip -->
        <slot name="overlay" />
      </template>

      <template #loading>
        <slot
          v-if="$slots.loading"
          name="loading"
        />

        <span
          v-else
          class="flex no-wrap"
        >
          <q-spinner-facebook
            v-if="loadingIconSide === 'left'"
            size="xs"
            class="on-left"
          />

          <span>{{ loadingLabel }}</span>

          <q-spinner-facebook
            v-if="loadingIconSide === 'right'"
            size="xs"
            class="on-right"
          />

          <q-spinner-facebook
            v-if="loadingIconSide === 'center'"
            size="xs"
            class="on-center"
          />
        </span>
      </template>
    </q-btn>
  </div>
</template>

<script setup lang="ts">
import type { PropType } from 'vue'
import { colors } from 'quasar'
import { computed, useSlots } from 'vue'

import { useReadOnly } from '@/composables/useReadOnly'

type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'
type LoadingIconSide = 'left' | 'right' | 'center'
type ButtonClass = string | unknown[] | Record<string, unknown> | null

const props = defineProps({
  noWrap: {
    type: Boolean,
    default: true,
  },
  color: {
    type: String,
    default: undefined,
  },
  textColor: {
    type: String,
    default: undefined,
  },
  noMinWidth: {
    type: Boolean,
    default: false,
  },
  loadingLabel: {
    type: String,
    default: '',
  },
  loadingIconSide: {
    type: String as PropType<LoadingIconSide>,
    default: 'left',
    validator: (value: string): value is LoadingIconSide => ['left', 'right', 'center'].includes(value),
  },
  label: {
    type: [String, Number],
    default: undefined,
  },
  size: {
    type: String as PropType<ButtonSize>,
    default: 'lg',
    validator: (value: string): value is ButtonSize => ['xs', 'sm', 'md', 'lg', 'xl'].includes(value),
  },
  flat: {
    type: Boolean,
    default: false,
  },
  xsFullWidth: {
    type: Boolean,
    default: false,
  },
  fullWidth: {
    type: Boolean,
    default: false,
  },
  stretch: {
    type: Boolean,
    default: false,
  },
  class: {
    type: [String, Array, Object] as PropType<ButtonClass>,
    default: null,
  },
  btnClasses: {
    type: String,
    default: null,
  },
  progressTime: {
    type: Number,
    default: 0,
  },
  isMultiLine: {
    type: Boolean,
    default: true,
  },
  icon: {
    type: String,
    default: null,
  },
  iconRight: {
    type: String,
    default: null,
  },
})

// This is needed to prevent Quasar from adding the $attrs to the root div instead of the q-btn:
defineOptions({ inheritAttrs: false })

const slots = useSlots()

const { isReadOnly } = useReadOnly()

const onlyContainsIcon = computed(
  () => (props.icon || props.iconRight)
    && !props.label
    && (!slots.default || !slots.overlay)
    && !props.fullWidth
    && !props.stretch,
)

const buttonPadding = computed(() => {
  const paddingMapSquare = {
    xs: '4px',
    sm: '8px',
    md: '10px',
    lg: '12px',
    xl: '14px',
  }

  const paddingMap = {
    xs: '4px 16px',
    sm: '8px 16px',
    md: '10px 16px',
    lg: '12px 16px',
    xl: '14px 16px',
  }

  return onlyContainsIcon.value ? paddingMapSquare[props.size] : paddingMap[props.size]
})

const stylingAttrs = computed(() => {
  if (!props.color) {
    return {
      outline: true,
      color: 'white',
      class: props.flat ? '' : 'bg-white re-default-border',
      textColor: props.textColor || 'accent',
    }
  }

  if (props.color === 'main') {
    return {
      filled: true,
      color: 'accent',
      textColor: props.textColor || 'white',
    }
  }

  let textColor = props.textColor
  // Determine text color based on background color brightness
  if (!textColor) {
    if (colors.brightness(colors.getPaletteColor(props.color)) > 155)
      textColor = 'dark'
    else textColor = 'white'
  }
  return { outline: false, filled: true, color: props.color, textColor }
})

const btnClasses = computed(() => {
  const classes = ['self-center']
  if (!props.noMinWidth && props.label)
    classes.push('re-btn-min-width')
  if (props.size)
    classes.push(`re-${props.size}`)
  if (props.xsFullWidth)
    classes.push('xs-full-width')
  if (props.fullWidth || props.stretch)
    classes.push('full-width')
  if (onlyContainsIcon.value)
    classes.push('re-btn-square')
  if (props.progressTime)
    classes.push('progress-time')
  if (props.isMultiLine)
    classes.push('text-multi-line')

  return [...classes, props.btnClasses]
})
</script>

<style lang="scss" scoped>
// NOTE: we set line-height here instead of $button-line-height to preserve
// default button icon appearance:
.q-btn {
  line-height: 1em;

  // :deep(.q-icon),
  // :deep(.q-spinner) {
  //   font-size: 1.3em;
  // }

  &--flat.disabled,
  &--flat[disabled] {
    background-color: var(--secondary-bground) !important;
    color: var(--secondary-light) !important;
  }

  &.re-default-border.q-btn--outline:before {
    border-color: $border;
  }

  &.progress-time {
    position: relative;
    overflow: hidden;

    :deep(.q-btn__content) {
      position: relative;
      z-index: 5;
      mix-blend-mode: difference;
      color: white;
    }

    &:after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      white-space: nowrap;
      height: 100%;
      background-color: black;
      z-index: 1;
      display: flex;
      align-items: center;
      pointer-events: none;
      justify-content: center;
      overflow: hidden;
      transform: translateX(-100%);
      animation-name: fill-background;
      animation-duration: var(--animation-duration);
      animation-timing-function: linear;
    }
  }
}

@keyframes fill-background {
  from {
    transform: translateX(-100%);
  }

  to {
    transform: translateX(0);
  }
}

body.screen--xs .q-btn.xs-full-width {
  width: 100%;
}

a.q-btn:hover {
  text-decoration: none !important;
}

.q-btn.re-btn-min-width {
  min-width: 6em;
}

.text-multi-line {
  height: auto;
  :deep(.q-btn__content) {
    white-space: pre-line;
  }
}

.q-btn.re-btn-square {
  min-width: 1em;
  aspect-ratio: 1;
}
</style>
